import React from 'react';

import { DollarOutlined, SwapOutlined } from '@ant-design/icons';

import Badge from '@/components/Badge';
import TableRowButtons from '@/components/Common/TableRowButtons';
import { CountryWithFlag } from '@/components/CountryFlag';
import { canSubmitManualPaymentClaim } from '@/pages/configurations/billing-subscription-payments/utils';
import { DEFAULT_EMPTY_VALUE } from '@/utils/constants';
import {
  createEnumOptions,
  getEnumTranslationKey,
  getEnumValue,
  parseOptionalNumericEnumFromApi,
} from '@/utils/core/data-transformation/enumHelpers';
import { CoreEnums } from '@/utils/domain/constants';
import { createTableActionsColumn, formatTZLocal, getEnumNamespace } from '@/utils/infrastructure';
import { createHighlightRender } from '@/utils/infrastructure/highlightColumns';
import Filters, { registerModuleFilters } from '@/utils/input/filters';
import { formatCurrency } from '@/utils/output/formatters';

import { MODEL_NAME } from './constants';

const { BaseFilters } = Filters;

export const formatMonthlyFeeWithCurrency = (monthlyFee, currency) => {
  const isoCurrency = getEnumValue(CoreEnums.Currency, currency) || 'USD';
  return formatCurrency(monthlyFee ?? 0, isoCurrency, {
    maximumFractionDigits: 2,
  });
};

const billingSubscriptionStatus = {
  [CoreEnums.BillingSubscriptionStatus.PendingActivation]: { status: 'info' },
  [CoreEnums.BillingSubscriptionStatus.PendingPayment]: { status: 'warning' },
  [CoreEnums.BillingSubscriptionStatus.Active]: { status: 'success' },
  [CoreEnums.BillingSubscriptionStatus.PastDue]: { status: 'execution' },
  [CoreEnums.BillingSubscriptionStatus.Canceled]: { status: 'canceled' },
  [CoreEnums.BillingSubscriptionStatus.Suspended]: { status: 'error' },
};

const ADMINISTRATION_FIELDS = new Set([
  'commercialActorKind',
  'commercialActorStatus',
  'descendantActors',
  'descendantCustomers',
  'suspendedAt',
  'suspensionReason',
  'suspendedBy',
  'lastReactivatedAt',
  'lastReactivatedBy',
]);

export const getSubscriptionPlanId = (subscription) =>
  subscription?.planId || subscription?.automyPlanId || subscription?.automyPlan?.id || null;

export const getSubscriptionPlanName = (subscription) =>
  subscription?.planName || subscription?.automyPlan?.name || '';

export const isSameSubscriptionPlan = (subscription, planId, planName) => {
  if (planId == null || planId === '') return false;
  const currentPlanId = getSubscriptionPlanId(subscription);
  if (currentPlanId != null && String(planId) === String(currentPlanId)) return true;
  const currentPlanName = getSubscriptionPlanName(subscription);
  return (
    currentPlanId == null &&
    !!currentPlanName &&
    !!planName &&
    String(planName) === String(currentPlanName)
  );
};

export const mergeBillingSubscriptionAdministration = (subscription, administration) => {
  if (!subscription || !administration) return subscription;
  if (
    subscription.id &&
    administration.id &&
    String(subscription.id) !== String(administration.id)
  ) {
    return subscription;
  }
  const planId = administration.planId ?? getSubscriptionPlanId(subscription);
  const planName = administration.planName ?? subscription?.automyPlan?.name;
  return {
    ...subscription,
    ...administration,
    administrationLoaded: true,
    ...(planName
      ? {
          automyPlan: {
            ...(subscription.automyPlan || {}),
            ...(planId ? { id: planId } : {}),
            name: planName,
          },
        }
      : {}),
  };
};

export const getSubscriptionAdministrationActionFlags = (administration) => ({
  canSuspend: administration?.administrationLoaded === true && administration?.canSuspend === true,
  canReactivate:
    administration?.administrationLoaded === true && administration?.canReactivate === true,
});

const buildBillingCountrySearchFilter = (searchTerm) => {
  const sanitized = searchTerm.replace(/'/g, "''");
  return `contains(name,'${sanitized}')`;
};

const StatusFilter = {
  ...BaseFilters.EnumFilter,
  name: 'status',
  label: 'global.text.status',
  enumNamespace: getEnumNamespace(CoreEnums.BillingSubscriptionStatus, 'Billing'),
  enumSource: CoreEnums.BillingSubscriptionStatus,
  options: createEnumOptions(CoreEnums.BillingSubscriptionStatus, 'billingSubscriptionStatus', {
    casing: 'camel',
  }),
  badgeMap: billingSubscriptionStatus,
  badgeType: 'group-item',
  filterSearch: true,
};

const CompanyFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'company',
  label: 'global.text.company',
  labelInValue: true,
  displayField: 'commercialName',
  apiRoute: 'company/paged',
  filterSearch: true,
  params: {
    select: 'id,commercialName',
    filter: 'isActive eq true',
    orderBy: 'commercialName asc',
  },
  oDataField: 'id',
};

const BillingCountryFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'billingCountryId',
  label: 'global.text.billingCountry',
  labelInValue: true,
  displayField: 'name',
  apiRoute: 'country/paged',
  dataCacheKey: 'billingCountryId',
  oDataField: 'billingCountryId',
  filterSearch: true,
  buildRemoteSearchFilter: buildBillingCountrySearchFilter,
  countryFlag: true,
  params: {
    select: 'id,name,code',
    orderBy: 'name asc',
  },
};

const BillingModeFilter = {
  ...BaseFilters.EnumFilter,
  name: 'billingMode',
  label: 'global.text.billingMode',
  enumNamespace: getEnumNamespace(CoreEnums.ChannelBillingMode, 'Channel.Financial'),
  enumSource: CoreEnums.ChannelBillingMode,
  options: createEnumOptions(CoreEnums.ChannelBillingMode, 'channelBillingMode', {
    casing: 'camel',
  }),
};

const CurrencyFilter = {
  ...BaseFilters.EnumFilter,
  name: 'currency',
  label: 'global.text.currency',
  enumNamespace: getEnumNamespace(CoreEnums.Currency),
  enumSource: CoreEnums.Currency,
  options: createEnumOptions(CoreEnums.Currency, 'currency', {
    casing: 'lower',
  }),
};

const BillingCycleTypeFilter = {
  ...BaseFilters.EnumFilter,
  name: 'billingCycleType',
  label: 'global.text.billingCycleType',
  enumNamespace: getEnumNamespace(CoreEnums.BillingCycleType, 'Billing'),
  enumSource: CoreEnums.BillingCycleType,
  options: createEnumOptions(CoreEnums.BillingCycleType, 'billingCycleType', {
    casing: 'camel',
  }),
};

const PaymentCollectionModeFilter = {
  ...BaseFilters.EnumFilter,
  name: 'paymentCollectionMode',
  label: 'global.text.paymentCollectionMode',
  enumNamespace: getEnumNamespace(CoreEnums.BillingCollectionMode, 'Billing'),
  enumSource: CoreEnums.BillingCollectionMode,
  options: createEnumOptions(CoreEnums.BillingCollectionMode, 'billingCollectionMode', {
    casing: 'camel',
  }),
};

const NextBillingDateFilter = {
  ...Filters.DateFilter,
  name: 'nextBillingDate',
  label: 'global.text.nextBillingDate',
  defaultValue: CoreEnums.FilterDateRange.Disabled,
};

const AutoRenewFilter = {
  ...BaseFilters.BooleanFilter,
  name: 'autoRenew',
  label: 'global.text.autoRenew',
  badgeOptions: {
    true: { status: 'success' },
    false: { status: 'default' },
  },
  badgeType: 'group-item',
};

const CreatedDateFilter = {
  ...Filters.DateFilter,
  name: 'created',
  label: 'global.created',
  defaultValue: CoreEnums.FilterDateRange.Disabled,
};

const UpdatedDateFilter = {
  ...Filters.DateFilter,
  name: 'updated',
  label: 'global.updated',
  defaultValue: CoreEnums.FilterDateRange.Disabled,
};

export const filtersConfig = [
  StatusFilter,
  CompanyFilter,
  BillingCountryFilter,
  BillingModeFilter,
  CurrencyFilter,
  BillingCycleTypeFilter,
  PaymentCollectionModeFilter,
  NextBillingDateFilter,
  AutoRenewFilter,
  CreatedDateFilter,
  UpdatedDateFilter,
];

registerModuleFilters(MODEL_NAME, filtersConfig);

/**
 * Domain detail mapping: list-matching badges for status/autoRenew,
 * monthly fee + currency, country flag. Other enum selects use `_form.js` locale.
 */
export function mapBillingSubscriptionDetailFields(rows, { item }) {
  if (!item || !rows?.length) return rows;

  return rows
    .filter((row) => {
      if (!ADMINISTRATION_FIELDS.has(row.name)) return true;
      if (!item.administrationLoaded) return false;
      if (
        (row.name === 'commercialActorKind' || row.name === 'commercialActorStatus') &&
        item[row.name] == null
      ) {
        return false;
      }
      if (row.name === 'monthlyIncludedCreditAmount' && item.monthlyIncludedCreditAmount == null) {
        return false;
      }
      return true;
    })
    .map((row) => {
      if (row.name === 'status') {
        const resolved = parseOptionalNumericEnumFromApi(
          CoreEnums.BillingSubscriptionStatus,
          item.status,
        );
        const config = resolved !== undefined ? billingSubscriptionStatus[resolved] : null;
        return {
          ...row,
          badgeStatus: config?.status || 'default',
          badgeType: 'group-item',
        };
      }

      if (row.name === 'commercialActorStatus') {
        const resolved = parseOptionalNumericEnumFromApi(
          CoreEnums.ChannelEntityStatus,
          item.commercialActorStatus,
        );
        const status =
          resolved === CoreEnums.ChannelEntityStatus.Active
            ? 'success'
            : resolved === CoreEnums.ChannelEntityStatus.Suspended
            ? 'error'
            : 'default';
        return {
          ...row,
          badgeStatus: status,
          badgeType: 'group-item',
        };
      }

      if (row.name === 'autoRenew') {
        const autoRenew = item.autoRenew === true;
        return {
          ...row,
          type: 'badge',
          badgeType: 'group-item',
          badgeStatus: autoRenew ? 'success' : 'default',
          value: autoRenew ? 'global.text.yes' : 'global.text.no',
          locale: true,
        };
      }

      if (row.name === 'monthlyFee') {
        if (item.monthlyFee == null) {
          return { ...row, value: null, label: 'global.text.subscriptionSubtotal' };
        }
        return {
          ...row,
          label: 'global.text.subscriptionSubtotal',
          value: formatMonthlyFeeWithCurrency(item.monthlyFee, item.currency),
        };
      }

      if (row.name === 'company.identity') {
        const identity = item?.company?.identity;
        if (identity == null || identity === '') {
          return { ...row, value: DEFAULT_EMPTY_VALUE };
        }
        return { ...row, value: identity };
      }

      if (row.name === 'billingCountry') {
        const country = item.billingCountry;
        const name = country?.name;
        if (!name) {
          return { ...row, value: null };
        }
        return { ...row, value: <CountryWithFlag code={country?.code}>{name}</CountryWithFlag> };
      }

      return row;
    });
}

export function buildBillingSubscriptionColumns({
  formatMessage,
  openDrawerCreateOrEdit,
  searchCriteria,
  openClaim,
  openChangePlan,
}) {
  const highlight = createHighlightRender(searchCriteria);

  const renderEnumBadge = (enumObject, value, translationPrefix) => {
    const resolved = parseOptionalNumericEnumFromApi(enumObject, value);
    if (resolved === undefined) {
      return <Badge status="default" type="process" text={DEFAULT_EMPTY_VALUE} />;
    }
    return (
      <Badge
        status="default"
        type="process"
        text={formatMessage({
          id: getEnumTranslationKey(enumObject, resolved, translationPrefix),
        })}
      />
    );
  };

  return [
    {
      key: 'status',
      title: formatMessage({ id: 'global.text.status' }),
      dataIndex: 'status',
      align: 'center',
      sorter: true,
      render: (value) => {
        const resolved = parseOptionalNumericEnumFromApi(
          CoreEnums.BillingSubscriptionStatus,
          value,
        );
        const config = resolved !== undefined ? billingSubscriptionStatus[resolved] : null;
        if (!config) {
          return <Badge status="default" type="group-item" text={DEFAULT_EMPTY_VALUE} />;
        }
        return (
          <Badge
            status={config.status}
            type="group-item"
            text={formatMessage({
              id: getEnumTranslationKey(
                CoreEnums.BillingSubscriptionStatus,
                resolved,
                'billingSubscriptionStatus',
              ),
            })}
          />
        );
      },
    },
    {
      key: 'automyPlan/name',
      title: formatMessage({ id: 'global.text.plan' }),
      dataIndex: ['automyPlan', 'name'],
      sorter: true,
      render: highlight,
    },
    {
      key: 'company/commercialName',
      title: formatMessage({ id: 'billingSubscriptions.columns.companyName' }),
      dataIndex: ['company', 'commercialName'],
      sorter: true,
      render: highlight,
    },
    {
      key: 'company/identity',
      title: formatMessage({ id: 'billingSubscriptions.columns.companyIdentity' }),
      dataIndex: ['company', 'identity'],
      sorter: true,
      render: (_, record) => {
        const identity = record?.company?.identity;
        if (identity == null || identity === '') return DEFAULT_EMPTY_VALUE;
        return highlight(identity, record);
      },
    },
    {
      key: 'billingMode',
      title: formatMessage({ id: 'global.text.billingMode' }),
      dataIndex: 'billingMode',
      align: 'center',
      sorter: true,
      render: (value) => renderEnumBadge(CoreEnums.ChannelBillingMode, value, 'channelBillingMode'),
    },
    {
      key: 'monthlyFee',
      title: formatMessage({ id: 'global.text.subscriptionSubtotal' }),
      dataIndex: 'monthlyFee',
      align: 'right',
      sorter: true,
      render: (_, record) => formatMonthlyFeeWithCurrency(record.monthlyFee, record.currency),
    },
    {
      key: 'billingCycleType',
      title: formatMessage({ id: 'global.text.billingCycleType' }),
      dataIndex: 'billingCycleType',
      align: 'center',
      sorter: true,
      render: (value) => renderEnumBadge(CoreEnums.BillingCycleType, value, 'billingCycleType'),
    },
    {
      key: 'paymentCollectionMode',
      title: formatMessage({ id: 'global.text.paymentCollectionMode' }),
      dataIndex: 'paymentCollectionMode',
      align: 'center',
      sorter: true,
      render: (value) =>
        renderEnumBadge(CoreEnums.BillingCollectionMode, value, 'billingCollectionMode'),
    },
    {
      key: 'nextBillingDate',
      title: formatMessage({ id: 'global.text.nextBillingDate' }),
      dataIndex: 'nextBillingDate',
      sorter: true,
      render: (value) => (value ? formatTZLocal(value) : DEFAULT_EMPTY_VALUE),
    },
    {
      key: 'autoRenew',
      title: formatMessage({ id: 'global.text.autoRenew' }),
      dataIndex: 'autoRenew',
      align: 'center',
      sorter: true,
      render: (value) => (
        <Badge
          status={value ? 'success' : 'default'}
          type="group-item"
          text={formatMessage({ id: value ? 'global.text.yes' : 'global.text.no' })}
        />
      ),
    },
    createTableActionsColumn({
      inlineActionCount: 1,
      render: (record) => {
        const customItems = [];
        if (canSubmitManualPaymentClaim(record) && openClaim) {
          customItems.push({
            key: 'register-payment',
            label: formatMessage({ id: 'billingSubscriptionPayments.claim.action' }),
            icon: <DollarOutlined />,
            onClick: (e) => {
              e?.preventDefault?.();
              e?.stopPropagation?.();
              openClaim(record);
            },
          });
        }
        if (record?.id && openChangePlan) {
          customItems.push({
            key: 'change-plan',
            label: formatMessage({ id: 'billingSubscriptions.changePlan.action' }),
            icon: <SwapOutlined />,
            onClick: (e) => {
              e?.preventDefault?.();
              e?.stopPropagation?.();
              openChangePlan(record);
            },
          });
        }
        return (
          <TableRowButtons
            onDetail={() => openDrawerCreateOrEdit(record.id, true)}
            formatMessage={formatMessage}
            customItems={customItems}
            actionOrder={{
              primary: ['detail'],
              secondary: customItems.map((item) => item.key),
            }}
          />
        );
      },
    }),
  ];
}
